import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaUtensils, FaCalculator, FaChartLine, FaLaptop, FaHotel, FaCocktail } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const HotellerieRestauration = () => {
    const [setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const servicesData = [
        {
            icon: <FaCalculator />,
            title: "Comptabilité, Fiscalité et Paie",
            description: "Gestion comptable adaptée à l'hôtellerie et la restauration, déclarations fiscales, gestion de la paie avec les spécificités du secteur.",
            color: "#0d6efd"
        },
        {
            icon: <FaChartLine />,
            title: "Gestion et Pilotage de la Performance",
            description: "Comptabilité analytique, tableaux de bord, suivi des KPIs spécifiques à votre activité, optimisation des coûts et des marges.",
            color: "#198754"
        },
        {
            icon: <FaLaptop />,
            title: "Conseil et Accompagnement Stratégique",
            description: "Création, reprise, développement d'établissement, évaluation, stratégie commerciale et transformation digitale.",
            color: "#dc3545"
        },
        {
            icon: <FaHotel />,
            title: "Expertise Sectorielle",
            description: "Une connaissance approfondie des enjeux et spécificités du secteur de l'hôtellerie et de la restauration.",
            color: "#6610f2"
        }
    ];

    const serviceDetails = {
        comptabilite: [
            {
                title: "Tenue et révision comptable",
                description: "Gestion rigoureuse de votre comptabilité, adaptée aux spécificités de votre activité (multi-établissements, multi-activités, etc.)."
            },
            {
                title: "Établissement des comptes annuels",
                description: "Bilan, compte de résultat, annexe, et production de liasses fiscales."
            },
            {
                title: "Gestion de la TVA",
                description: "Maîtrise des différents taux de TVA applicables (denrées alimentaires, hébergement, etc.), déclarations et télétransmissions."
            },
            {
                title: "Optimisation fiscale",
                description: "Recherche des meilleures options pour réduire votre charge fiscale (crédits d'impôt, exonérations, etc.)."
            },
            {
                title: "Déclarations fiscales et sociales",
                description: "Prise en charge de l'ensemble de vos déclarations."
            },
            {
                title: "Gestion de la paie et du social",
                description: "Établissement des bulletins de paie, déclarations sociales, gestion des contrats de travail (extras, saisonniers, apprentis, etc.), accompagnement en cas de contrôle de la CNSS."
            },
            {
                title: "Assistance en cas de contrôle fiscal",
                description: "Accompagnement complet et défense de vos intérêts."
            }
        ],
        gestion: [
            {
                title: "Comptabilité analytique et contrôle de gestion",
                description: "Mise en place d'outils de suivi précis de vos coûts (food cost, beverage cost, etc.), de vos marges par produit/service, et de votre rentabilité."
            },
            {
                title: "Tableaux de bord et indicateurs clés de performance (KPI)",
                description: "Suivi en temps réel de vos performances (chiffre d'affaires, taux de marge brute, masse salariale, etc.)."
            },
            {
                title: "Budgets prévisionnels",
                description: "Élaboration de budgets réalistes et suivi budgétaire rigoureux."
            },
            {
                title: "Analyse financière et diagnostic",
                description: "Identification des forces, faiblesses, opportunités et menaces (SWOT) de votre établissement."
            },
            {
                title: "Optimisation des coûts",
                description: "Identification des gisements d'économies (achats, personnel, énergie, etc.) et mise en place de plans d'action."
            },
            {
                title: "Gestion de trésorerie",
                description: "Prévisions, optimisation des encaissements et décaissements, recherche de solutions de financement à court terme."
            },
            {
                title: "Benchmarking",
                description: "Comparaison de vos performances avec celles de vos concurrents."
            }
        ],
        conseil: [
            {
                title: "Accompagnement à la création et à la reprise d'établissement",
                description: "Étude de marché, business plan, choix de la forme juridique, recherche de financements, formalités administratives."
            },
            {
                title: "Conseil en investissement",
                description: "Analyse de la rentabilité de vos projets (rénovation, extension, acquisition de matériel, etc.), recherche de subventions."
            },
            {
                title: "Conseil en financement",
                description: "Optimisation de votre structure financière, recherche de financements (prêts bancaires, crédit-bail, etc.)."
            },
            {
                title: "Évaluation d'établissement",
                description: "Dans le cadre d'une cession, d'une acquisition, d'une transmission ou d'une restructuration."
            },
            {
                title: "Stratégie de développement",
                description: "Identification des opportunités de croissance (nouveaux concepts, diversification, développement de la franchise, etc.)."
            },
            {
                title: "Conseil en gestion des ressources humaines",
                description: "Recrutement, fidélisation du personnel, optimisation des plannings, gestion des conflits."
            },
            {
                title: "Accompagnement à la transformation digitale",
                description: "Mise en place de solutions de réservation en ligne, de gestion de la relation client (CRM), d'e-réputation, etc."
            },
            {
                title: "Conseil en matière de réglementation",
                description: "Hygiène et sécurité alimentaire (HACCP), normes d'accessibilité, affichages obligatoires, etc."
            }
        ]
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-12">
                            <h1 className="fw-bold display-4 mb-3">Expertise Comptable et Conseil pour l'Hôtellerie et la Restauration</h1>
                            <p className="fs-5 mb-4">Optimisez Votre Rentabilité, Maîtrisez Vos Coûts, Développez Votre Activité</p>
                            <nav aria-label="breadcrumb">

                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                CINEF-EC comprend les défis uniques de l'hôtellerie et de la restauration
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Le secteur de l'hôtellerie et de la restauration est exigeant : forte concurrence, fluctuations saisonnières, 
                                gestion des stocks, personnel important, réglementations strictes... CINEF-EC connaît parfaitement vos enjeux. 
                                Nous sommes bien plus qu'un cabinet d'expertise comptable ; nous sommes votre partenaire stratégique pour vous aider 
                                à gérer votre établissement avec succès, de la comptabilité quotidienne à la réalisation de vos projets de développement.
                            </p>
                        </div>
                    </div>

                    <div className="row align-items-center">
                        <div className="col-lg-12">
                            <h3 className="fw-bold custom-title mb-4">Une expertise dédiée au secteur CHR</h3>
                            <p className="mb-4">
                                Notre cabinet dispose d'une expertise particulière dans l'accompagnement des professionnels de l'hôtellerie et de la restauration.
                                Nous connaissons parfaitement les spécificités opérationnelles, financières et réglementaires de votre secteur.
                            </p>
                            <p className="mb-4">
                                Notre objectif est de vous accompagner dans l'optimisation de votre rentabilité, la maîtrise de vos coûts
                                et le développement de votre activité dans un environnement concurrentiel.
                            </p>
                            <div className="d-flex mt-4">
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaHotel className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Hôtels</span>
                                </div>
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaUtensils className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Restaurants</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaCocktail className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Bars & Café</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold custom-title mb-5">Nos services pour les professionnels de l'hôtellerie et de la restauration</h2>
                        </div>
                    </div>
                    <div className="row">
                        {servicesData.map((service, index) => (
                            <div className="col-md-6 col-lg-3 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">1. Comptabilité, Fiscalité et Paie</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.comptabilite.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`comptabilite-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">2. Gestion et Pilotage de la Performance</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.gestion.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`gestion-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">3. Conseil et Accompagnement Stratégique</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.conseil.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`conseil-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Vous souhaitez optimiser la gestion de votre établissement et développer votre activité ?</h3>
                                <p className="text-white fs-5 mb-4">
                                    Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée.
                                    Nous analyserons ensemble vos besoins et vous proposerons des solutions adaptées à votre situation.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un entretien gratuit</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/hotel-restaurant-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
        </>
    );
};

export default HotellerieRestauration;